import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Loader2, X, MapPin, Phone, Briefcase, BadgeCheck, Filter, RefreshCw } from 'lucide-react';
import useAutoRefresh from '../../lib/useAutoRefresh';
import SearchableSelect from '../ui/SearchableSelect';
import HealthMetricsWidget from './HealthMetricsWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Live Ops Dashboard — 3 tabs:
 *   • Attendance
 *   • Day Activities
 *   • Visit Breakup
 *
 * Each tab fetches its own slice from `/admin/ops-dashboard?section=...`.
 * Visit Breakup runs a per-staff GPS query so it's deferred until the user
 * actually opens that tab, keeping the default page load fast.
 *
 * Every numeric cell is clickable and centre-aligned for easy scanning.
 */

const TABS = [
  {
    id: 'attendance',
    title: 'Attendance',
    color: 'bg-slate-100',
    columns: [
      { key: 'total_staff', label: 'Total Staff (active)' },
      { key: 'present_today', label: 'Present' },
      { key: 'on_time_present', label: 'On-time Present', accent: 'bg-emerald-50' },
      { key: 'checkin_outside_geofence', label: 'Check-in Outside Geofence' },
      { key: 'checked_out', label: 'Checked Out', accent: 'bg-indigo-50' },
      { key: 'checkout_outside_geofence', label: 'Checkout Outside Geofence', accent: 'bg-indigo-50' },
      { key: 'checkout_before_schedule', label: 'Checkout Before Schedule', accent: 'bg-indigo-50' },
      { key: 'absent', label: 'Absent' },
      { key: 'absent_no_info', label: 'Absent w/o Info' },
      { key: 'late_10', label: '0–10 m Late' },
      { key: 'late_20', label: '10–20 m Late' },
      { key: 'late_30', label: '20–30 m Late' },
      { key: 'late_45', label: '30–45 m Late' },
      { key: 'late_60', label: '45–60 m Late' },
      { key: 'late_above_60', label: '> 1 hr Late' },
    ],
  },
  {
    id: 'activities',
    title: 'Day Activities',
    color: 'bg-blue-50',
    // Custom layout — rendered via DayActivitiesPanel below, not the
    // generic SectionTable. Columns listed here just feed the legacy
    // flat-table fallback used by the StaffPopup (drill-down stays the same).
    customLayout: 'day_activities',
    columns: [
      { key: 'present_active', label: 'Present (Active)' },
      { key: 'within_geofence', label: 'Within Geofence', accent: 'bg-sky-100' },
      { key: 'outside_geofence', label: 'Outside Geofence', accent: 'bg-sky-100' },
      { key: 'inside_missing_gps', label: 'Missing GPS', accent: 'bg-rose-100' },
      { key: 'inside_in_office', label: 'Inside · In Office', accent: 'bg-emerald-50' },
      { key: 'inside_in_tea', label: 'Inside · Tea', accent: 'bg-emerald-50' },
      { key: 'inside_in_lunch', label: 'Inside · Lunch', accent: 'bg-emerald-50' },
      { key: 'inside_in_permission', label: 'Inside · Permission', accent: 'bg-emerald-50' },
      { key: 'outside_in_visit', label: 'Outside · In Visit', accent: 'bg-amber-50' },
      { key: 'outside_no_plan', label: 'Outside · No Plan', accent: 'bg-amber-50' },
    ],
  },
  {
    id: 'visit_breakup',
    title: 'In Visit Breakup',
    color: 'bg-amber-50',
    columns: [
      { key: 'visit_in_visit', label: 'In Visit', accent: 'bg-yellow-100' },
      { key: 'visit_in_drive', label: 'In Drive', accent: 'bg-yellow-100' },
      { key: 'visit_at_supplier', label: 'At Supplier', accent: 'bg-yellow-100' },
      { key: 'supplier_20m', label: '> 20 m at supplier', accent: 'bg-yellow-100' },
      { key: 'supplier_30m', label: '> 30 m', accent: 'bg-yellow-100' },
      { key: 'supplier_45m', label: '> 45 m', accent: 'bg-yellow-100' },
      { key: 'supplier_60m', label: '> 1 hr', accent: 'bg-yellow-100' },
      { key: 'visit_at_selected_supplier', label: 'At Selected Supplier', accent: 'bg-yellow-100' },
      { key: 'visit_not_at_selected_supplier', label: 'Not at Selected Sup.', accent: 'bg-yellow-100' },
      { key: 'visit_in_between_stopped', label: 'In-between Stopped' },
    ],
  },
];

export default function OpsDashboard() {
  // Date picker default = today (IST `YYYY-MM-DD`). The backend matches
  // `attendance.date` against this so historical days reuse the same path.
  //
  // PRIOR BUG: this used to be
  //   `d.getTime() + 5.5*3600*1000 - d.getTimezoneOffset()*60*1000`
  // which applied a DOUBLE timezone shift when the browser was already in
  // IST (`getTimezoneOffset()` returns -330 there, so subtracting that adds
  // another +5:30). At ≥ 18:30 IST, the result rolled to TOMORROW's date —
  // exactly what the user reported as "today date is showing tomorrow".
  //
  // Correct logic: add 5.5 h to the UTC ms and read the date portion.
  // Equivalent to what `toLocaleDateString('en-CA', {timeZone:'Asia/Kolkata'})`
  // produces.
  const todayStr = () => {
    const d = new Date();
    const istMs = d.getTime() + (5.5 * 60 * 60 * 1000);
    return new Date(istMs).toISOString().slice(0, 10);
  };
  const [activeTab, setActiveTab] = useState('attendance');
  const [dataByTab, setDataByTab] = useState({});           // { [tabId]: payload }
  const [loadingByTab, setLoadingByTab] = useState({});      // { [tabId]: bool }
  const [filters, setFilters] = useState({
    geo_fence_id: '', department_id: '', designation_id: '',
    date: todayStr(),
  });
  const [popup, setPopup] = useState(null);                  // { title, staffIds }

  const isHistorical = filters.date !== todayStr();

  const fetchTab = useCallback(async (tabId, { verifyMissing = false } = {}) => {
    setLoadingByTab(prev => ({ ...prev, [tabId]: true }));
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams({ section: tabId });
      if (filters.geo_fence_id) qs.set('geo_fence_id', filters.geo_fence_id);
      if (filters.department_id) qs.set('department_id', filters.department_id);
      if (filters.designation_id) qs.set('designation_id', filters.designation_id);
      if (filters.date) qs.set('date', filters.date);
      if (verifyMissing) qs.set('verify_missing', 'true');
      const r = await fetch(`${API_URL}/api/admin/ops-dashboard?${qs.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const payload = await r.json();
        setDataByTab(prev => ({ ...prev, [tabId]: payload }));
      }
    } catch (e) {
      console.error('[OpsDashboard]', tabId, e);
    } finally {
      setLoadingByTab(prev => ({ ...prev, [tabId]: false }));
    }
  }, [filters]);

  // Fetch the active tab whenever it changes or filters change.
  useEffect(() => { fetchTab(activeTab); }, [activeTab, fetchTab]);

  // Auto-refresh only the currently active tab — and only when viewing today.
  // Historical dates are static, so re-fetching on a timer just wastes calls.
  useAutoRefresh(() => { if (!isHistorical) fetchTab(activeTab); }, 7_000);

  const data = dataByTab[activeTab];
  const loading = loadingByTab[activeTab] && !data;

  const directory = data?.directory || {};
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const staffForCell = useMemo(() => {
    if (!popup) return [];
    return popup.staffIds.map(id => directory[id]).filter(Boolean);
  }, [popup, directory]);

  const openCell = (geoName, colLabel, ids) => {
    if (!ids || ids.length === 0) return;
    setPopup({ title: `${colLabel} · ${geoName}`, staffIds: ids });
  };

  const activeSection = TABS.find(t => t.id === activeTab);

  // Filter dropdown options come from whichever tab payload is loaded first.
  const filterOpts = data?.filters
    || dataByTab.attendance?.filters
    || dataByTab.activities?.filters
    || dataByTab.visit_breakup?.filters
    || { geo_fences: [], departments: [], designations: [] };

  return (
    <div data-testid="ops-dashboard" className="space-y-4">
      <HealthMetricsWidget />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isHistorical ? `Ops Dashboard — ${filters.date}` : 'Live Ops Dashboard'}
          </h1>
          <p className="text-xs text-gray-500">
            {isHistorical ? 'Historical view · ' : 'Auto-refreshes every 7 s · '}
            {data?.generated_at && <>Last updated {new Date(data.generated_at).toLocaleTimeString('en-IN')}</>}
          </p>
        </div>
        <button
          onClick={() => fetchTab(activeTab)}
          disabled={loadingByTab[activeTab]}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          data-testid="ops-refresh"
        >
          <RefreshCw size={14} className={loadingByTab[activeTab] ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-3 flex flex-wrap gap-3 items-center" data-testid="ops-filters">
        <div className="flex items-center gap-1 text-gray-500 text-xs"><Filter size={14} /> Filters</div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <span className="font-medium">Date</span>
          <input
            type="date"
            value={filters.date}
            max={todayStr()}
            onChange={(e) => setFilters({ ...filters, date: e.target.value || todayStr() })}
            className="px-2 py-1 border rounded text-sm"
            data-testid="ops-filter-date"
          />
        </label>
        {isHistorical && (
          <button
            onClick={() => setFilters({ ...filters, date: todayStr() })}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
            data-testid="ops-filter-today"
          >
            Back to today
          </button>
        )}
        <SearchableSelect
          value={filters.geo_fence_id}
          onChange={(v) => setFilters({ ...filters, geo_fence_id: v })}
          options={[{ value: '', label: 'All Geo Fences' }, ...filterOpts.geo_fences.map(g => ({ value: g.id, label: g.name }))]}
          placeholder="All Geo Fences"
          testid="ops-filter-geo"
          allowClear
          minWidth={180}
        />
        <SearchableSelect
          value={filters.department_id}
          onChange={(v) => setFilters({ ...filters, department_id: v })}
          options={[{ value: '', label: 'All Departments' }, ...filterOpts.departments.map(d => ({ value: d.id, label: d.name }))]}
          placeholder="All Departments"
          testid="ops-filter-dept"
          allowClear
          minWidth={180}
        />
        <SearchableSelect
          value={filters.designation_id}
          onChange={(v) => setFilters({ ...filters, designation_id: v })}
          options={[{ value: '', label: 'All Designations' }, ...filterOpts.designations.map(d => ({ value: d.id, label: d.name }))]}
          placeholder="All Designations"
          testid="ops-filter-desig"
          allowClear
          minWidth={180}
        />
        {(filters.geo_fence_id || filters.department_id || filters.designation_id) && (
          <button
            onClick={() => setFilters({ ...filters, geo_fence_id: '', department_id: '', designation_id: '' })}
            className="text-xs text-gray-600 hover:text-gray-900 underline"
            data-testid="ops-filter-clear"
          >Clear filters</button>
        )}
      </div>

      {isHistorical && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
          data-testid="ops-historical-banner"
        >
          <span className="text-base leading-none">📅</span>
          <span className="flex-1">
            Showing <strong>historical attendance</strong> for {filters.date}. Day Activities and Visit Breakup are live-only — switch <strong>Back to today</strong> to see ongoing visits/breaks/GPS.
          </span>
        </div>
      )}

      {/* Tab strip */}
      <div className="flex flex-wrap gap-2 border-b" data-testid="ops-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === t.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            data-testid={`ops-tab-${t.id}`}
          >
            {t.title}
            {loadingByTab[t.id] && activeTab === t.id && (
              <Loader2 size={12} className="inline ml-2 animate-spin" />
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500" data-testid="ops-loading">
          <Loader2 className="animate-spin mr-2" /> Loading {activeSection?.title}…
        </div>
      ) : !data ? (
        <div className="p-6 text-red-600">Could not load this section.</div>
      ) : activeSection?.customLayout === 'day_activities' ? (
        <DayActivitiesPanel
          rows={rows}
          totals={totals}
          directory={directory}
          loading={loadingByTab[activeTab]}
          onCellClick={openCell}
          onVerifyMissing={() => fetchTab('activities', { verifyMissing: true })}
        />
      ) : (
        <SectionTable
          section={activeSection}
          rows={rows}
          totals={totals}
          onCellClick={openCell}
        />
      )}

      {popup && (
        <StaffPopup
          title={popup.title}
          staff={staffForCell}
          onClose={() => setPopup(null)}
        />
      )}
    </div>
  );
}

function SectionTable({ section, rows, totals, onCellClick }) {
  const allRows = [...rows, totals];
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid={`ops-section-${section.id}`}>
      <div className={`px-4 py-2 ${section.color} border-b font-semibold text-sm text-gray-800`}>
        {section.title}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase whitespace-nowrap">Geo Fence</th>
              {section.columns.map(c => (
                <th
                  key={c.key}
                  className={`px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase whitespace-nowrap ${c.accent || ''}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {allRows.map((r, i) => {
              const isTotal = r.geo_fence_id === '__total';
              return (
                <tr key={r.geo_fence_id || i} className={isTotal ? 'bg-gray-50 font-bold' : ''}>
                  <td className="px-3 py-2 whitespace-nowrap">{r.geo_fence_name}</td>
                  {section.columns.map(c => {
                    const val = r[c.key] ?? 0;
                    const ids = r[`${c.key}__staff_ids`] || [];
                    const interactive = val > 0;
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-2 text-center tabular-nums ${c.accent || ''} ${interactive ? 'cursor-pointer hover:bg-blue-100' : 'text-gray-400'}`}
                        onClick={() => onCellClick(r.geo_fence_name, c.label, ids)}
                        data-testid={`ops-cell-${section.id}-${r.geo_fence_id}-${c.key}`}
                      >
                        {val}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// Day Activities — dual Inside/Outside breakup tables + Missing-Staff
// auto-verify (Points #6 + #7, Feb 22, 2026).
// =====================================================================

const INSIDE_COLS = [
  { key: 'inside_in_office',     label: 'In Office' },
  { key: 'inside_in_tea',        label: 'In Tea' },
  { key: 'inside_in_lunch',      label: 'In Lunch' },
  { key: 'inside_in_permission', label: 'On Permission' },
];

const OUTSIDE_COLS = [
  { key: 'outside_in_visit',  label: 'In Visit' },
  { key: 'outside_no_plan',   label: 'No Plan / Unauth.' },
];

function DayActivitiesPanel({ rows, totals, directory, loading, onCellClick, onVerifyMissing }) {
  const totalPresent = totals.present_active || 0;
  const totalInside  = totals.within_geofence || 0;
  const totalOutside = totals.outside_geofence || 0;
  const totalMissing = totals.inside_missing_gps || 0;
  const totalClassified = totalInside + totalOutside;
  const invariantHolds = totalPresent === (totalClassified + totalMissing);

  // Auto-trigger the 7-s server-side verify pass when missing > 0 OR
  // when the invariant doesn't hold. Run at most ONCE per data snapshot
  // (guarded by `verifiedAtRef`).
  const verifiedAtRef = React.useRef(null);
  const generatedAt = totals?.generated_at; // unique per fetch
  React.useEffect(() => {
    const snapshot = `${generatedAt}|${totalMissing}`;
    if (
      totalMissing > 0 &&
      verifiedAtRef.current !== snapshot &&
      !loading
    ) {
      verifiedAtRef.current = snapshot;
      onVerifyMissing();
    }
  }, [generatedAt, totalMissing, loading, onVerifyMissing]);

  const missingStaffIds = totals.inside_missing_gps__staff_ids || [];
  const missingStaff = missingStaffIds.map((id) => directory[id]).filter(Boolean);

  return (
    <div className="space-y-4" data-testid="ops-section-activities">
      <SummaryStrip
        present={totalPresent}
        inside={totalInside}
        outside={totalOutside}
        missing={totalMissing}
        invariantHolds={invariantHolds}
      />

      <div className="grid lg:grid-cols-2 gap-4">
        <BreakupTable
          variant="inside"
          title="Inside Geofence"
          accent="bg-emerald-50"
          totalKey="within_geofence"
          cols={INSIDE_COLS}
          rows={rows}
          totals={totals}
          onCellClick={onCellClick}
        />
        <BreakupTable
          variant="outside"
          title="Outside Geofence"
          accent="bg-amber-50"
          totalKey="outside_geofence"
          cols={OUTSIDE_COLS}
          rows={rows}
          totals={totals}
          onCellClick={onCellClick}
        />
      </div>

      <MissingStaffReport
        loading={loading}
        invariantHolds={invariantHolds}
        totalPresent={totalPresent}
        totalClassified={totalClassified}
        totalMissing={totalMissing}
        staff={missingStaff}
        onRerun={onVerifyMissing}
      />
    </div>
  );
}

function SummaryStrip({ present, inside, outside, missing, invariantHolds }) {
  const sum = inside + outside + missing;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="day-activities-summary">
      <SummaryCard label="Present (Active)" value={present} tone="bg-slate-100 text-slate-900" testid="da-present" />
      <SummaryCard label="Inside Geofence" value={inside} tone="bg-emerald-50 text-emerald-900" testid="da-inside" />
      <SummaryCard label="Outside Geofence" value={outside} tone="bg-amber-50 text-amber-900" testid="da-outside" />
      <SummaryCard label="Missing GPS" value={missing} tone={missing > 0 ? 'bg-rose-50 text-rose-900' : 'bg-gray-50 text-gray-500'} testid="da-missing" />
      <SummaryCard
        label="Inside + Outside + Missing"
        value={sum}
        tone={invariantHolds ? 'bg-blue-50 text-blue-900' : 'bg-red-50 text-red-900'}
        testid="da-sum"
        subtext={invariantHolds ? '= Present ✓' : `≠ ${present} ✗`}
      />
    </div>
  );
}

function SummaryCard({ label, value, tone, subtext, testid }) {
  return (
    <div className={`rounded-xl p-3 ${tone}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums leading-snug">{value}</div>
      {subtext && <div className="text-[11px] opacity-70">{subtext}</div>}
    </div>
  );
}

function BreakupTable({ variant, title, accent, totalKey, cols, rows, totals, onCellClick }) {
  const allRows = [...rows, totals];
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid={`day-activities-${variant}-table`}>
      <div className={`px-4 py-2 ${accent} border-b font-semibold text-sm text-gray-800 flex items-center justify-between`}>
        <span>{title}</span>
        <span className="text-xs font-normal text-gray-600">Total: <b className="tabular-nums">{totals[totalKey] || 0}</b></span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase whitespace-nowrap">Geo Fence</th>
              <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase whitespace-nowrap bg-sky-50">Total</th>
              {cols.map(c => (
                <th key={c.key} className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase whitespace-nowrap">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {allRows.map((r, i) => {
              const isTotal = r.geo_fence_id === '__total';
              const tot = r[totalKey] || 0;
              return (
                <tr key={r.geo_fence_id || i} className={isTotal ? 'bg-gray-50 font-bold' : ''}>
                  <td className="px-3 py-2 whitespace-nowrap">{r.geo_fence_name}</td>
                  <td
                    className={`px-3 py-2 text-center tabular-nums bg-sky-50 ${tot > 0 ? 'cursor-pointer hover:bg-sky-100' : 'text-gray-400'}`}
                    onClick={() => onCellClick(r.geo_fence_name, title, r[`${totalKey}__staff_ids`] || [])}
                    data-testid={`da-${variant}-cell-${r.geo_fence_id}-total`}
                  >
                    {tot}
                  </td>
                  {cols.map(c => {
                    const v = r[c.key] || 0;
                    const ids = r[`${c.key}__staff_ids`] || [];
                    const interactive = v > 0;
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-2 text-center tabular-nums ${interactive ? 'cursor-pointer hover:bg-blue-100' : 'text-gray-400'}`}
                        onClick={() => onCellClick(r.geo_fence_name, c.label, ids)}
                        data-testid={`da-${variant}-cell-${r.geo_fence_id}-${c.key}`}
                      >
                        {v}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MissingStaffReport({ loading, invariantHolds, totalPresent, totalClassified, totalMissing, staff, onRerun }) {
  if (totalPresent === 0) return null;
  const allOK = invariantHolds && totalMissing === 0;
  return (
    <div
      className={`rounded-xl border p-4 ${allOK ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
      data-testid="missing-staff-report"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h3 className={`font-semibold text-sm ${allOK ? 'text-emerald-900' : 'text-rose-900'}`}>
            {allOK
              ? 'Totals reconciled · all present staff classified Inside / Outside ✓'
              : `Totals mismatch · ${totalMissing} staff missing GPS / not classified`}
          </h3>
          <p className={`text-xs mt-0.5 ${allOK ? 'text-emerald-800' : 'text-rose-800'}`}>
            Present (Active) <b>{totalPresent}</b> = Inside + Outside <b>{totalClassified}</b> + Missing <b>{totalMissing}</b>
          </p>
        </div>
        {!allOK && (
          <button
            onClick={onRerun}
            disabled={loading}
            className="text-xs px-3 py-1.5 bg-white border border-rose-300 hover:bg-rose-100 disabled:opacity-50 rounded text-rose-800 font-medium"
            data-testid="missing-rerun-verify"
          >
            {loading ? 'Verifying…' : '🔄 Re-run 7-sec GPS verify'}
          </button>
        )}
      </div>
      {!allOK && staff.length > 0 && (
        <div className="mt-2">
          <div className="text-[11px] uppercase tracking-wide text-rose-700 mb-1">Missing-GPS staff (last ping &gt; 15 min ago)</div>
          <div className="bg-white border border-rose-200 rounded-lg divide-y divide-rose-100 max-h-60 overflow-y-auto" data-testid="missing-staff-list">
            {staff.map((s) => (
              <div key={s.id} className="px-3 py-2 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 text-xs">
                <span className="font-semibold col-span-2 md:col-span-1">{s.name} <span className="text-gray-400 font-mono">{s.staff_id || ''}</span></span>
                <span className="text-gray-700">{s.mobile || '—'}</span>
                <span className="text-gray-700">{s.geo_fence_name || '—'}</span>
                <span className="text-gray-700">{s.department || '—'} · {s.designation || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StaffPopup({ title, staff, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4" data-testid="ops-popup">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 truncate">{title} <span className="text-gray-500 font-normal">· {staff.length} staff</span></h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="ops-popup-close">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto p-3">
          {staff.length === 0 && <p className="text-center text-gray-500 py-8">No staff in this bucket.</p>}
          <div className="divide-y divide-gray-100">
            {staff.map((s) => (
              <div key={s.id} className="py-2.5 grid grid-cols-2 md:grid-cols-3 gap-y-1 gap-x-3 text-sm" data-testid={`ops-popup-row-${s.id}`}>
                <div className="font-semibold text-gray-900 col-span-2 md:col-span-1">{s.name} <span className="text-gray-400 font-mono text-xs">{s.staff_id || s.id}</span></div>
                <div className="text-gray-700 inline-flex items-center gap-1"><Phone size={12} /> {s.mobile || '—'}</div>
                <div className="text-gray-700 inline-flex items-center gap-1"><MapPin size={12} /> {s.geo_fence_name || '—'}</div>
                <div className="text-gray-700 inline-flex items-center gap-1"><Briefcase size={12} /> {s.department || '—'}</div>
                <div className="text-gray-700 inline-flex items-center gap-1"><BadgeCheck size={12} /> {s.designation || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
